/**
 * What a priced service looks like once it has left the database and is ready
 * to render — the shape `/pl/montaz`, `/pl/grafika` and the creator all read.
 *
 * Prices arrive as BOTH a formatted label and a number. The label is what the
 * card shows; the number is what the basket and the configurator do arithmetic
 * with. They used to be one string that every consumer re-parsed with a regex,
 * which is how "Wycena indywidualna" once became 0 zł and discounted a basket.
 * A quote-only tier now carries `quoteOnly: true` and a `priceNet` of 0 that no
 * code path is allowed to treat as a price.
 */

import type { StudioDay, StudioWeekday } from '@repo/core/studio-calendar'
import type { ComparisonRow } from '@/lib/tiers'

export type { ComparisonRow } from '@/lib/tiers'
export { formatPLN, sortRowsForTier } from '@/lib/tiers'

export type PlanView = {
  /** Tier slug — what an order line resolves back to. */
  slug: string
  /** Display name, also the cart line label. */
  name: string
  description: string
  /** Formatted for the eye, e.g. "219 zł" or "Wycena indywidualna". */
  price: string
  /** NET złoty as a number; 0 when `quoteOnly`, and then never used as a price. */
  priceNet: number
  /** Unit suffix as shown, e.g. "/ rolka". Empty when the tier has none. */
  suffix: string
  popular: boolean
  quoteOnly: boolean
  /**
   * WHERE this level happens — for the two that are a meeting.
   *
   * `/pl/strategia` sells one hour at one price in two places, which is how the
   * owner asked for it: *„tak naprawdę to 1 pozycja, różnica taka że jedno
   * online drugie stacjonarnie"*. The page turns these into the two tiles, and
   * the checkout copies the chosen one onto the reservation so the office knows
   * whether to send a link or open the door.
   */
  meetingPlace?: 'ONLINE' | 'OFFICE'
  /**
   * The first day this level could actually start, from the team's declared
   * capacity (spec §6). Three states, and all three mean something different:
   *
   * `undefined` — nothing to say. The level has no daily calendar: a quote, or
   * an offering the customer picks dates for.
   * `null` — asked, and there is no room anywhere in the horizon. Nobody has
   * declared time for this level, or all of it is sold. The card must not offer
   * it (owner's rule: "sprzedajemy tylko to, co mamy na stanie").
   * a day — that day.
   *
   * Read fresh on every request, so a performer adding a week to their grid
   * turns the button back on without anybody deploying anything.
   */
  readyFrom?: string | null
  /**
   * The soonest INSTANT a finished piece can be collected: the first free slot plus
   * the level's own promise to the client.
   *
   * It REPLACED „Start od" on the card. Owner, 14.10: *„usuwamy »Start od« —
   * interesuje nas tylko termin odbioru"*. A start date answers a question about
   * the team's calendar; the person reading three prices is asking about theirs.
   *
   * The LATE end of the window, not the early one — 48 h is a promise, and a
   * promise is the day you are held to, not the day you hope for. The slot still
   * moves as other orders take capacity, so the day shown on Tuesday is not the
   * day somebody ordering on Thursday will be given.
   *
   * `undefined` where there is nothing to compute: a level with no daily
   * calendar, or one whose offering has no client-side turnaround. `null` where
   * the level was asked about and has no room at all, exactly as `readyFrom`.
   */
  deliveryAt?: string | null
}

/**
 * Something a client can ask to have quoted, with no price of its own — a
 * wizytówka, a logo, an ad set. The page collects the selection and hands it to
 * an enquiry; it must not invent a number, which is why there is no price here
 * to accidentally render.
 */
export type QuoteOptionView = {
  slug: string
  label: string
  blurb: string
  /** The catch-all: renders last and asks for a note. */
  isCustom: boolean
}

export type ServiceView = {
  /** Service slug — the cart's service id, so lines merge across pages. */
  slug: string
  name: string
  blurb: string
  /** The marketing page selling it — where the creator sends "zobacz zakres". */
  href: string
  /** Polish plural forms: 1 / 2–4 / 5+. Falls back to the unit label. */
  unitForms: [string, string, string]
  /** Label above the quantity control, e.g. "Ile rolek?". */
  quantityLabel: string
  defaultQty: number
  plans: PlanView[]
  /** The comparison matrix, keyed by tier NAME — see `ComparisonRow`. */
  rows: ComparisonRow[]
  quoteOptions: QuoteOptionView[]
}

/** What a quote-only tier says instead of a number. */
export const QUOTE_LABEL = 'Wycena indywidualna'

/**
 * Offerings the catalogue still carries but the site no longer sells, because
 * something narrower took their place.
 *
 * `film` is the generic "Montaż" the named categories were split out of. They
 * are copies of it, so leaving it on the page put the same product in front of
 * the customer twice — once by name and once as "everything else" — and made
 * the first tab a synonym for all the others.
 *
 * In CODE and not in the catalogue: retiring the row is panel work against a
 * live database, it takes the offering out of the creator and every other
 * surface at the same time, and a deploy cannot carry it. This withdraws it
 * from ONE page, which is what was asked for, and leaves that wider decision
 * where it belongs — with somebody who can see what else still points at it.
 *
 * There was a second list beside this one, `WITHDRAWN_SLUGS`, holding Newsy and
 * Vlogi: the owner had taken them off the page but they were still in the
 * catalogue and still reachable from the creator. On 14.10 he asked for them to
 * go for good („wszędzie gdzie to jest, jest usuwane"), so both rows were
 * deleted from the database — nothing had ever been ordered on them — and the
 * list went with them. The distinction the two lists drew still matters, which
 * is why this comment records it: „the owner took it out of the offer" and „it
 * was a duplicate" lead to different decisions.
 */
export const SUPERSEDED_SLUGS: readonly string[] = ['film']

/** Whether the site has stopped selling this offering by name. */
export function isSuperseded(service: ServiceView): boolean {
  return SUPERSEDED_SLUGS.includes(service.slug)
}

/**
 * Offerings sold ONLY by enquiry, whatever packages they still carry.
 *
 * `grafika` is one row holding two different things: a Starter / Pro / Brand
 * ladder, and the nine options quoted by enquiry. The ladder is out of the
 * offer; the options are the whole of `/pl/grafika`. Deleting the offering
 * would take the options down with it, so the ladder is withdrawn at the point
 * of sale instead.
 *
 * This governs what the SITE offers. The variants still exist in the catalogue
 * until somebody retires them in the panel, which is worth doing so the creator
 * cannot reach them either.
 */
export const QUOTE_ONLY_SLUGS: readonly string[] = ['grafika']

/** Whether this service still has packages a page may put a price on. */
export function sellsPackages(service: ServiceView): boolean {
  return service.plans.length > 0 && !QUOTE_ONLY_SLUGS.includes(service.slug)
}

/**
 * One bookable room on `/pl/studio`. It carries its OWN blocks: before the
 * catalogue merged, every room shared a single generic "Studio nagrań" price
 * list, so two rooms could not be priced apart even though the page published
 * two different hourly rates.
 */
/** One bookable window of studio time, as the booking panel offers it. */
export type StudioBlockView = {
  /** The variant slug — what the creator link preselects. */
  slug: string
  /** „3 godziny", „Cały dzień, 8 h". */
  name: string
  /** Formatted total, e.g. „660 zł". */
  price: string
  /** Whole hours, for the last-start arithmetic. */
  hours: number
}

export type StudioRoomView = {
  slug: string
  kicker: string
  name: string
  tagline: string
  description: string
  capacityLabel: string
  /** Shortest bookable block, in minutes. */
  minBookingMinutes: number
  /**
   * The cheapest HOUR in the room, NET złoty — the rate the longest block
   * works out at (owner's call: *„pokazujemy od najniższej"*).
   *
   * Not the cheapest block: the ladder's totals rise with length while the rate
   * inside them falls, so „od 280 zł" (the hour) and „od 180 zł za h" (the day)
   * are both true and only the second is a floor. `null` when nothing in the
   * room is priced by a window — then the card falls back to the cheapest
   * figure it can find.
   */
  fromHourlyNet: number | null
  /**
   * The blocks somebody can actually book, shortest first.
   *
   * `plans` carries the same rows, but not how LONG each one is — `PlanView` is
   * the marketing shape and a level has no duration. The booker needs it for
   * arithmetic: the last start of a day is its closing time minus the block, so
   * an 8 h block on a Saturday that shuts at 22:00 cannot begin after 14:00.
   */
  blocks: readonly StudioBlockView[]
  /**
   * The days this room can still be booked on, and what is free on each.
   *
   * Two things the page could not see before it carried this: the calendar the
   * panel PUBLISHES (spec §10 · I1 makes it the only source of bookable hours)
   * and what is already RESERVED. The picker used to be drawn from a weekly
   * rule in the bundle, so it offered hours the office had not opened and hours
   * somebody already held, and the refusal came at checkout.
   *
   * Warsaw wall-clock minutes past midnight, half-open — the conversion happens
   * once, on the server. Empty means "nothing bookable", which is also what a
   * failed read produces, deliberately.
   */
  calendar: readonly StudioDay[]
  /**
   * The published week, for the line under the grid.
   *
   * A different question from `calendar` and deliberately a separate field:
   * free days shrink as reservations land, so „Godziny otwarcia" derived from
   * them would announce a nine o'clock opening because somebody booked a
   * Monday morning.
   */
  week: readonly StudioWeekday[]
  /** What the room includes — the bullet list on its card. */
  features: string[]
  plans: PlanView[]
  image: string | null
  imageAlt: string | null
}
